import React, { useEffect, useRef, useState } from 'react';
import './chat-bot.scss';
import { useParams } from 'react-router-dom';
import { AppResultInfo } from '../../types/decisions';
import type { RankFilters } from '../../api/decisions.api';
import { rankApps } from '../../api/decisions.api';
import AppResults from '../application/app-components/AppResults/AppResults';
import { LANGUAGES, LANGUAGE_MAP} from './languages';
import { TRANSLATIONS } from './translations';

const transformCriteria = (criteria: RankFilters['criteria']): RankFilters['criteria'] => {
  return criteria.map(item => ({
    ...item,
    value: item.value === '' ? '-' : item.value,
  }));
};

const ChatWithTheo = () => {
  const [messages, setMessages] = useState<{ text: string; sender: 'user' | 'theo' }[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingText, setTypingText] = useState('Theo is typing');
  const [results, setResults] = useState<AppResultInfo[]>([]);
  const getInitialLanguage = () => {
    const browserLang = navigator.language || navigator.languages[0] || 'en';
    const shortCode = browserLang.split('-')[0].toLowerCase(); // e.g., "en-US" → "en"
    const mappedLang = LANGUAGE_MAP[shortCode]; // e.g., "English"
  
    return LANGUAGES.includes(mappedLang) ? mappedLang : 'English';
  };
  
  
  const [selectedLanguage, setSelectedLanguage] = useState(getInitialLanguage());
  
  const [isChatStarted, setIsChatStarted] = useState(false);const [showDropdown, setShowDropdown] = useState(false);

  const t = (key: string) => TRANSLATIONS[key]?.[selectedLanguage] || TRANSLATIONS[key]?.['English'];

  const ws = useRef<WebSocket | null>(null);
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);
  const typingDots = useRef<NodeJS.Timeout | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  const { id } = useParams();

  useEffect(() => {
    if (!selectedLanguage || !id) return;

    ws.current = new WebSocket('wss://theo-backend.dev.sparkdit.com/ws/chat');

    ws.current.onopen = () => {
      console.log('Connected to backend');
      ws.current?.send(JSON.stringify({
        type: 'init',
        app_id: Number(id),
        language: selectedLanguage,
      }));
      setIsChatStarted(true);
    };

    ws.current.onmessage = async (event) => {
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
        typingTimeout.current = null;
      }

      const data = safeJsonParse(event.data);

      if (data && data.type === 'final_rank') {
        const rank = data.rank as RankFilters;
        try {
          const ranked = await rankApps({
            id: String(id),
            criteria: transformCriteria(rank.criteria),
            constraints: rank.constraints || null,
          }, 3, 1);

          setResults(ranked.results || []);
          setMessages(prev => [...prev, { text: "✅ Here are three top recommendations.", sender: 'theo' }]);
        } catch (err) {
          console.error("Failed to fetch ranked results", err);
          setMessages(prev => [...prev, { text: "❌ Sorry, I couldn't fetch the results.", sender: 'theo' }]);
        }
        return;
      }

      setTimeout(() => {
        stopTyping();
        setMessages(prev => [...prev, { text: event.data, sender: 'theo' }]);
      }, 800);
    };

    return () => {
      ws.current?.close();
      clearTimeout(typingTimeout.current!);
      clearInterval(typingDots.current!);
    };
  }, [id, selectedLanguage]);

  const sendMessage = () => {
    if (!input.trim()) return;

    setMessages(prev => [...prev, { text: input, sender: 'user' }]);
    ws.current?.send(input);
    setInput('');

    typingTimeout.current = setTimeout(() => {
      startTyping();
    }, 2000);
  };

  const startTyping = () => {
    setIsTyping(true);
    let dots = '';
    typingDots.current = setInterval(() => {
      dots = dots.length < 3 ? dots + '.' : '';
      setTypingText(`Theo is typing${dots}`);
    }, 500);
  };

  const stopTyping = () => {
    setIsTyping(false);
    clearInterval(typingDots.current!);
    typingDots.current = null;
  };



  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isTyping]);
  
  

  const safeJsonParse = (msg: string) => {
    try {
      return JSON.parse(msg);
    } catch {
      return null;
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-box">
        <div className="chat-header">
          <div className="title">{t('chatTitle')}</div>
          <div className="language-selector">
            <button
              className="lang-button"
              onClick={() => setShowDropdown(prev => !prev)}
              aria-label="Select Language"
            >
              🌐
            </button>
            {showDropdown && (
              <select
                className="lang-dropdown"
                value={selectedLanguage}
                onChange={(e) => {
                  setSelectedLanguage(e.target.value);
                  setShowDropdown(false);
                }}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            )}
          </div>
        </div>
  
        <div className="messages" ref={messagesRef}>
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.sender}`}>
              {msg.text}
            </div>
          ))}
          {isTyping && <div className="message theo">{typingText}</div>}
        </div>

  
        <div className="input-container">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder={t('inputPlaceholder')}
            disabled={!isChatStarted}
          />
          <button onClick={sendMessage} disabled={!isChatStarted}>
            {t('sendButton')}
          </button>
        </div>
        
      </div>

      {/* Move results outside the chat box here */}
      {results.length > 0 && (
        <div className="results-section">
          <AppResults
            results={results}
            pageInfo={{
              page_size: 1,
              current_page: 1,
              start: 1,
              total_pages: 1,
              total: results.length,
              end: results.length,
            }}
            onPageChange={() => {}}
            onRerank={() => {}}
            onRerankMany={() => {}}
            selectedProductIds={[]}
            setSelectedProductIds={() => {}}
            allowedFieldsIds={(results[0]?.nonTechnicalFields || []).map(f => f.id)}
          />
        </div>
      )}
  
      
    </div>
  );
  
};

export default ChatWithTheo;
